import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { FiShoppingBag, FiUsers, FiAlertCircle, FiBarChart2 } from 'react-icons/fi';
import ErrorBoundary from '../components/ErrorBoundary';
import PaymentAnalytics from '../components/PaymentAnalytics';
import MemberAnalytics from '../components/MemberAnalytics';

const DashboardContent = () => {
    const [stats, setStats] = useState({
        totalShops: 0,
        activeShops: 0,
        inactiveShops: 0,
        premiumShops: 0,
        closedShops: 0,
        featureShops: 0,
        totalMembers: 0,
        activeMembers: 0,
        holdMembers: 0,
        cancelledMembers: 0,
        expectedRenewalAmount: 0,
        expectedRenewalMembers: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const [shopsResponse, membersResponse, analyticsResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/api/shops/stats`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/godmembers/stats`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/api/analytics/next-month`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    })
                ]);

                if (shopsResponse.data.success && membersResponse.data.success && analyticsResponse.data.success) {
                    setStats({
                        ...shopsResponse.data.data,
                        totalMembers: membersResponse.data.data.totalMembers,
                        activeMembers: membersResponse.data.data.activeMembers,
                        holdMembers: membersResponse.data.data.holdMembers,
                        cancelledMembers: membersResponse.data.data.cancelledMembers,
                        expectedRenewalAmount: analyticsResponse.data.data.expectedRenewalAmount,
                        expectedRenewalMembers: analyticsResponse.data.data.expectedRenewalMembers
                    });
                }
            } catch (err) {
                setError(`Failed to fetch statistics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full max-w-none px-8">
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-6">Dashboard Overview</h2>

                {error && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md flex items-center">
                        <FiAlertCircle className="mr-2" />
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Total Shops Card */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiShoppingBag className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-blue-800">Total Shops</h3>
                                <p className="text-3xl font-bold text-blue-600">{stats.totalShops}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Active</span>
                                <span className="font-medium text-blue-800">{stats.activeShops}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-700">Inactive</span>
                                <span className="font-medium text-gray-800">{stats.inactiveShops}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-red-700">Closed</span>
                                <span className="font-medium text-red-800">{stats.closedShops}</span>
                            </div>
                        </div>
                    </div>

                    {/* Members Card */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiUsers className="h-8 w-8 text-purple-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-purple-800">Total Members</h3>
                                <p className="text-3xl font-bold text-purple-600">{stats.totalMembers}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Active</span>
                                <span className="font-medium text-purple-800">{stats.activeMembers}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Hold</span>
                                <span className="font-medium text-purple-800">{stats.holdMembers}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-purple-700">Cancelled</span>
                                <span className="font-medium text-purple-800">{stats.cancelledMembers}</span>
                            </div>
                        </div>
                    </div>

                    {/* Data Analytics Card */}
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiBarChart2 className="h-8 w-8 text-yellow-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-yellow-800">Next Month Analytics</h3>
                                <p className="text-sm font-bold text-yellow-600">{new Date().getMonth() === 11 ? `January ${new Date().getFullYear() + 1}` : `${new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(new Date().setMonth(new Date().getMonth() + 1)))} ${new Date().getFullYear()}`}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-yellow-700">Active Member till Date</span>
                                <span className="font-medium text-yellow-800">{stats.activeMembers}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-yellow-700">Expected Renewals</span>
                                <span className="font-medium text-yellow-800">{stats.expectedRenewalMembers}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-yellow-700">Expected Revenue</span>
                                <span className="font-medium text-yellow-800">
                                    {new Intl.NumberFormat('en-SG', {
                                        style: 'currency',
                                        currency: 'SGD'
                                    }).format(stats.expectedRenewalAmount)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Analytics Section */}
            <PaymentAnalytics />

            {/* Member Analytics Section */}
            <div className="pb-8">
                <MemberAnalytics />
            </div>
        </div>
        
    );
};

const Dashboard = () => {
    return (
        <Layout>
            <ErrorBoundary
                fallbackTitle="Dashboard Error"
                fallbackDescription="An error occurred while loading the dashboard. Please try again or contact support if the problem persists."
            >
                <DashboardContent />
            </ErrorBoundary>
        </Layout>
    );
};

export default Dashboard; 